import { z } from "zod";
import { createEndpoint } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import { ExpertQuickSearchResultSchema, type ExpertQuickSearchResult } from "../schemas/expert-quick-search";
import { ShopifyPartnerTier, ShopifyPartnerTierSchema } from "../schemas";
import {
    AdminExpertConversionStatsSchema,
    SwitchExpertTypeResponseSchema,
    type SwitchExpertTypeRequest,
    type SwitchExpertTypeResponse,
} from "../schemas/expert-manager";

export interface ExpertQuickSearchQueryParams extends QueryParams {
    query?: string;
    status?: ExpertQuickSearchStatus[];
}

export const EXPERT_QUICK_SEARCH_STATUS = {
    APPLICATION: "application",
    DRAFT: "draft",
    PUBLISHED: "published",
    UNPUBLISHED: "unpublished",
    BANNED: "banned",
} as const;

export type ExpertQuickSearchStatus =
    (typeof EXPERT_QUICK_SEARCH_STATUS)[keyof typeof EXPERT_QUICK_SEARCH_STATUS];

export interface UpdateExpertTierRequest {
    partner_tier: ShopifyPartnerTier;
}

export interface UpdateExpertTierData {
    expert_id: number;
    partner_tier: ShopifyPartnerTier;
}

const UpdateExpertTierDataSchema = z.object({
    expert_id: z.number(),
    partner_tier: ShopifyPartnerTierSchema,
});

export function createAdminExpertApi(http: HttpClient, baseUrl: string) {
    const adminExpertBaseUrl = `${baseUrl}/admin/experts`;
    
    return {
        quickSearch: createEndpoint<
            ExpertQuickSearchResult[],
            unknown,
            ExpertQuickSearchQueryParams
        >(http, "GET", "/quick-search", {
            base: adminExpertBaseUrl,
            responseSchema: z.array(ExpertQuickSearchResultSchema),
        }),
        updateTier: createEndpoint<
            UpdateExpertTierData,
            UpdateExpertTierRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "PUT", "/:expertId/tier", {
            base: adminExpertBaseUrl,
            responseSchema: UpdateExpertTierDataSchema,
            params: ['expertId'],
        }),
        switchType: createEndpoint<
            SwitchExpertTypeResponse,
            SwitchExpertTypeRequest,
            QueryParams,
            { expertId: number | string }
        >(http, "POST", "/:expertId/type", {
            base: adminExpertBaseUrl,
            responseSchema: SwitchExpertTypeResponseSchema,
            params: ['expertId'],
        }),
        getConversionStats: createEndpoint<
            z.infer<typeof AdminExpertConversionStatsSchema>,
            unknown,
            QueryParams,
            { expertId: number | string }
        >(http, "GET", "/:expertId/conversion-stats", {
            base: adminExpertBaseUrl,
            responseSchema: AdminExpertConversionStatsSchema,
            params: ['expertId'],
        }),
    };
}
